import classNames from 'classnames';
import type { OverlayScrollbarsComponentRef } from 'overlayscrollbars-react';
import type { Op } from 'quill/core';
import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from '../../app/redux/useAppSelector';
import ScrollArea from '../../components/ScrollArea';
import Toast from '../../components/Toast';
import { CREATION_ID, EditorActions } from '../../editor/editorSlice';
import type { DocumentType } from '../../graphql/generated/gql';
import { gql } from '../../graphql/generated/gql';
import type { Comment_CommentFragment } from '../../graphql/generated/graphql';
import { UIActions } from '../../ui/uiSlice';
import HoverContentPreview from '../HoverContentPreview';
import Comment, { COMMENT_ANIMATION_DURATION } from './Comment';
import CommentCreate from './CommentCreate';
import * as styles from './CommentThreadFull.module.scss';
import FoldedComments from './FoldedComments';
import useResolveThread from './useResolveThread';
import addTestId from '../../utils/addTestId';
import type { ExtractCommentThreadTypes } from '../../utils/post/threadUtils';

const COMMENT_LIMIT = 2;

const POST_FRAGMENT = gql(`
  fragment CommentThreadFull_post on RootPostFields {
    id
    ...Comment_post
    ...CommentCreate_post
  }
`);

const THREAD_FRAGMENT = gql(`
  fragment CommentThreadFull_thread on RootThreadFields {
    id
    ...on CommentThread {
      comments {
        id
        ...Comment_comment
      }
    }
    ...on PublicCommentThread {
      comments {
        id
        ...Comment_comment
      }
    }
    ...CommentCreate_thread
    ...useResolveThread_thread
  }
`);

interface CommentThreadFullContainerProps {
  expanded: boolean;
  children: React.ReactNode;
  input?: React.ReactNode;
  scrollAreaRef?: React.RefObject<OverlayScrollbarsComponentRef>;
  showThread?: boolean;
}

export const CommentThreadFullContainer = ({
  children,
  expanded,
  scrollAreaRef,
  showThread,
  input,
}: CommentThreadFullContainerProps) => {
  const [isAtBottom, setIsAtBottom] = useState(false);
  const [hasOverflow, setHasOverflow] = useState(false);

  return (
    <div
      className={classNames(styles.commentThreadFull, {
        [styles.expanded]: expanded,
      })}
    >
      {showThread && (
        <ScrollArea
          ref={scrollAreaRef}
          events={{
            scroll: (event) => {
              const { viewport } = event.elements();
              setIsAtBottom(
                Math.ceil(viewport.scrollTop + viewport.clientHeight) >=
                  viewport.scrollHeight,
              );
            },
            updated: (event) => {
              setHasOverflow(event.state().hasOverflow.y);
            },
          }}
        >
          {expanded ? (
            children
          ) : (
            <HoverContentPreview.DisableHoverBoundary>
              {children}
            </HoverContentPreview.DisableHoverBoundary>
          )}
        </ScrollArea>
      )}
      {expanded && !!input && (
        <div
          {...addTestId('new-comment-input')}
          className={classNames(styles.create, {
            [styles.hasMoreScrollContent]: hasOverflow && !isAtBottom,
          })}
        >
          {input}
        </div>
      )}
    </div>
  );
};

export interface CommentThreadFullProps {
  thread: ExtractCommentThreadTypes<typeof THREAD_FRAGMENT>;
  post: DocumentType<typeof POST_FRAGMENT>;
  isExpanded?: boolean;
  hovered?: boolean;
  initialShowingMore?: boolean;
  onCreated?: () => void;
  createComment: React.ComponentProps<typeof CommentCreate>['createComment'];
  addComment: React.ComponentProps<typeof CommentCreate>['addComment'];
  deleteThread: React.ComponentProps<typeof Comment>['deleteThread'];
  toggleCommentReaction: (
    comment: ExtractCommentThreadTypes<
      typeof THREAD_FRAGMENT
    >['comments'][number],
    emoji: string,
  ) => void;
  deleteComment: (commentId: string) => void;
  onResize?: () => void;
  unsavedInputValue?: Op[];
  onInputChange?: (content: Op[]) => void;
  unresolveThread: (threadId: string) => Promise<unknown>;
  wide?: boolean;
}

const CommentThreadFull = ({
  thread,
  post,
  isExpanded = false,
  hovered,
  initialShowingMore = false,
  onCreated,
  createComment,
  addComment,
  deleteThread,
  deleteComment,
  toggleCommentReaction,
  onResize = () => {},
  unsavedInputValue = [],
  onInputChange,
  unresolveThread,
  wide,
}: CommentThreadFullProps) => {
  const { id: threadId, comments } = thread;

  const dispatch = useAppDispatch();

  const resolveThread = useResolveThread(thread);

  const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
  const scrollAreaRef = useRef<OverlayScrollbarsComponentRef>(null);

  const isCreating = threadId === CREATION_ID;

  const isShowingThread = !isCreating && comments && comments.length > 0;

  const handleCreated = () => {
    onCreated?.();

    setTimeout(() => {
      const viewport = scrollAreaRef.current?.osInstance()?.elements().viewport;
      if (viewport) {
        viewport.scrollTop = viewport.scrollHeight;
      }
    }, COMMENT_ANIMATION_DURATION * 1000);
  };

  useEffect(() => {
    onResize();

    if (!isExpanded) {
      setEditingCommentId(null);
    }
  }, [isExpanded, onResize]);

  const handleResolve = async () => {
    await resolveThread();

    dispatch(
      UIActions.pushToast(({ onDismiss }) => (
        <Toast
          level="success"
          onDismiss={onDismiss}
          message="Thread resolved"
          secondaryText="Undo"
          onSecondaryClick={() => {
            onDismiss();
            unresolveThread(thread.id);
          }}
        />
      )),
    );
  };

  const onEdit = (commentId: string) => {
    setEditingCommentId(commentId);
    dispatch(
      EditorActions.focusMagneticWall({
        id: thread.id,
        type: 'comment',
        isActive: true,
        hasExpandIntent: true,
      }),
    );
    onResize();
  };

  return (
    <CommentThreadFullContainer
      expanded={isExpanded}
      showThread={isShowingThread}
      scrollAreaRef={scrollAreaRef}
      input={
        <CommentCreate
          post={post}
          thread={thread}
          onChange={onInputChange}
          onCreated={handleCreated}
          onResize={onResize}
          initialContent={unsavedInputValue}
          disabled={editingCommentId !== null}
          createComment={createComment}
          addComment={addComment}
        />
      }
    >
      <FoldedComments.Root<Comment_CommentFragment>
        comments={comments}
        limit={isExpanded ? Infinity : COMMENT_LIMIT}
        expanded={isExpanded}
        showLineWhenFolded
        renderComment={(comment, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === comments.length - 1;

          return (
            <Comment
              key={comment.id}
              expanded={isExpanded}
              hasExtraBottomPadding={isLast && isExpanded}
              first={isFirst}
              last={isLast}
              initialShowingMore={initialShowingMore}
              onResize={onResize}
              comment={comment}
              thread={thread}
              post={post}
              onResolve={handleResolve}
              editing={editingCommentId === comment.id}
              onEdit={() => {
                onEdit(comment.id);
              }}
              onCancelEdit={() => {
                setEditingCommentId(null);
                onResize();
              }}
              disabledAnimatedPreview={wide}
              threadHovered={!!hovered}
              deleteThread={deleteThread}
              deleteComment={() => deleteComment(comment.id)}
              toggleCommentReaction={(emoji) =>
                toggleCommentReaction(comment, emoji)
              }
            />
          );
        }}
      />
    </CommentThreadFullContainer>
  );
};

export default CommentThreadFull;
